import { useState, type ChangeEvent } from "react";
import type { Combatant } from "@lancer-vtt/domain";
import { ALLOWED_UPLOAD_CONTENT_TYPES, MAX_UPLOAD_BYTES, type Result } from "@lancer-vtt/shared";
import { uploadTokenImage } from "../../api";
import type { EncounterDispatch } from "../StatusEditor";

/** The upload the control calls; a prop so a render test can stand in for the network. */
export type TokenImageUpload = (args: { encounterId: string; combatantId: string; file: File }) => Promise<Result<string, string>>;

/**
 * The GM's **Token image** control on the EDIT unit inspector (`projects/token-art.md` Phase 3.2):
 * upload a picture for this unit's board token, or clear the one set. Uploads go to
 * `<world>/uploads/token-images/` and the returned URL is written with `set_token_image`, which is
 * GM-only at the server gate and undoable. The EDIT desktop is GM-only at the host clamp, so this
 * control carries no role check of its own; `actorId === ""` disables it like the other EDIT fields.
 *
 * It sets only the override. A unit without one keeps its resolved art (roster upload, chassis
 * token, box art, generic ring), and a player seeing a hostile through a disclosure mask never gets
 * the override (`tokenImageFor`).
 */
export function TokenImageControl({
  combatant: c,
  encounterId,
  actorId,
  dispatch,
  upload = uploadTokenImage,
}: {
  combatant: Combatant;
  encounterId: string;
  actorId: string;
  dispatch: EncounterDispatch;
  upload?: TokenImageUpload;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const off = !actorId || busy;

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be picked again after an error
    if (!file) return;
    setError(null);
    if (!(ALLOWED_UPLOAD_CONTENT_TYPES as readonly string[]).includes(file.type)) {
      setError("Use a PNG, JPG, or WEBP image.");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setError("Image too large (max 5 MB).");
      return;
    }
    setBusy(true);
    const uploaded = await upload({ encounterId, combatantId: c.id, file });
    setBusy(false);
    if (!uploaded.ok) {
      setError(uploaded.error);
      return;
    }
    dispatch({ type: "set_token_image", actorId, combatantId: c.id, image: uploaded.value });
  }

  return (
    <div className="npc-ctl-edit-field cw-token-image">
      <span className="npc-ctl-label">Token image</span>
      <div className="cw-token-image-row">
        <span className="cw-token-image-preview" aria-hidden="true">
          {c.tokenImage ? <img src={c.tokenImage} alt="" /> : <span className="cw-token-image-none">auto</span>}
        </span>
        <label className={`npc-ctl-edit-delete cw-token-image-btn${off ? " is-off" : ""}`}>
          {busy ? "Uploading…" : "Upload"}
          <input
            type="file"
            hidden
            accept={ALLOWED_UPLOAD_CONTENT_TYPES.join(",")}
            disabled={off}
            aria-label={`Upload a token image for ${c.name}`}
            onChange={onFile}
          />
        </label>
        <button
          type="button"
          className="npc-ctl-edit-delete"
          disabled={off || !c.tokenImage}
          title={`Clear ${c.name}'s token image; it falls back to its roster or chassis art`}
          onClick={() => dispatch({ type: "set_token_image", actorId, combatantId: c.id })}
        >
          Clear
        </button>
      </div>
      {error && <span className="cw-token-image-error" role="alert">{error}</span>}
    </div>
  );
}
